// Typed wrapper around GET /api/tests/{id}/evaluation — the
// consolidated study-evaluation rollup (Slice 2 of the Virtual Rig
// Study reframe). The backend computes, in one fetch:
//   - the study's resolved Objective (or null before one is chosen),
//   - per COMPLETE run: the fidelity Guardrails (the single source of
//     truth from damperlib.guardrail_evaluators.evaluate_guardrails),
//     the objective score, and the objective delta vs the Reference
//     run (run_number == 0; null on the Reference run itself).
//
// Wire shape mirrors damperlib/api_models.py StudyEvaluation /
// RunEvaluation / StudyEvaluationObjective, and the guardrails dict
// mirrors guardrail_evaluators.GuardrailResult projected to JSON.
// Style follows ./runs.ts (Zod-parse the response, throw on non-2xx).
import { z } from "zod";

// One per-corner fidelity row, from GuardrailResult.per_corner.
// delta_mean_mm is null on replay runs (no recorded mean to compare);
// it may also be a non-finite number (NaN) when the metric is present
// but unusable — z.number() accepts that, the corner's *_pass flag
// carries the verdict either way.
export const GuardrailPerCornerSchema = z.object({
  corner: z.string(),
  r2_dyn: z.number().nullable(),
  r2_dyn_pass: z.boolean(),
  delta_mean_mm: z.number().nullable(),
  delta_mean_pass: z.boolean(),
  rmse_m: z.number().nullable(),
  // False when the corner contributed NO gating check (track-replay run:
  // no recorded damper truth at all) — its *_pass flags are then
  // exemption-shaped true and the row renders N/A, not PASS.
  // Optional+default(true) tolerates payloads predating the tri-state.
  evaluable: z.boolean().optional().default(true),
});

export const GuardrailsSchema = z.object({
  overall_pass: z.boolean(),
  // Honest tri-state (2026-06-11): NOT_EVALUABLE = nothing was gateable
  // (replay run, no recorded truth) — distinct from FAIL. Optional so
  // payloads predating the field degrade to the PASS/FAIL boolean.
  verdict: z.enum(["PASS", "FAIL", "NOT_EVALUABLE"]).optional(),
  per_corner: z.array(GuardrailPerCornerSchema),
});

// Per-run rollup. guardrails is null when the run has no
// kpi_summary_json artifact; objective_* are null when no objective is
// resolved / the run lacks that score / on the Reference run.
// Slice 3: when a run used a Study-level tyre override differing from the
// event default, the runner stamps this descriptor; counterfactual=true makes
// recorded-truth fidelity informational for that run.
export const CounterfactualTyreSchema = z.object({
  model: z.string(),
  counterfactual: z.boolean(),
  note: z.string().optional(),
});

// 2026-06-11 replay-guardrails build: the run's engineering-validity
// block (written by the runner into kpi_summary when per-Study limits
// resolved). overall_state BREACH = the run's ABSOLUTE excursion exceeded
// a physical limit (engineer information — under prescribed z_pad this is
// input-dominated); the AR gate itself is the baseline-relative
// constraint set on the objective. A SEPARATE axis from the fidelity
// tri-state above. Passthrough-shaped: bounds/limits stay loose records
// so backend additions don't break the parse.
export const ValiditySchema = z.object({
  overall_state: z.enum(["OK", "BREACH", "NOT_EVALUABLE"]),
  bounds: z.record(z.string(), z.record(z.string(), z.unknown())),
  limits: z.record(z.string(), z.unknown()),
});

export const RunEvaluationSchema = z.object({
  guardrails: GuardrailsSchema.nullable(),
  objective_score: z.number().nullable(),
  objective_delta_vs_reference: z.number().nullable(),
  counterfactual_tyre: CounterfactualTyreSchema.nullable().optional(),
  // Null for runs predating the build / without resolved limits.
  validity: ValiditySchema.nullable().optional(),
});

export const StudyEvaluationObjectiveSchema = z.object({
  objective_id: z.string(),
  display_name: z.string(),
  primary_kpi_id: z.string().nullable(),
  direction: z.enum(["min", "max", "target"]),
});

export const StudyEvaluationSchema = z.object({
  objective: StudyEvaluationObjectiveSchema.nullable(),
  // Keyed by stringified run_id; complete-only.
  runs: z.record(z.string(), RunEvaluationSchema),
});

export type GuardrailPerCorner = z.infer<typeof GuardrailPerCornerSchema>;
export type Guardrails = z.infer<typeof GuardrailsSchema>;

export type GuardrailVerdict = "PASS" | "FAIL" | "NOT_EVALUABLE";

// Tri-state guardrail verdict; payloads predating the field degrade to
// the PASS/FAIL boolean (overall_pass alone cannot express N/A).
export function guardrailVerdict(g: Guardrails): GuardrailVerdict {
  return g.verdict ?? (g.overall_pass ? "PASS" : "FAIL");
}
export type Validity = z.infer<typeof ValiditySchema>;
export type RunEvaluation = z.infer<typeof RunEvaluationSchema>;
export type StudyEvaluationObjective = z.infer<
  typeof StudyEvaluationObjectiveSchema
>;
export type StudyEvaluation = z.infer<typeof StudyEvaluationSchema>;

export async function fetchStudyEvaluation(
  testId: number,
): Promise<StudyEvaluation> {
  const r = await fetch(`/api/tests/${testId}/evaluation`);
  if (!r.ok) {
    throw new Error(`GET /api/tests/${testId}/evaluation failed: ${r.status}`);
  }
  return StudyEvaluationSchema.parse(await r.json());
}
